import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { MediaUpload } from '@/components/MediaUpload';
import { Select } from '@/components/Select';
import { getTrackerPwas, createTrackerPwa, updateTrackerPwa, deleteTrackerPwa } from '@/api/tracker-pwas';
import { uploadPwaMedia, translateDescriptions, translateReviews } from '@/api/pwa';
import ReviewsEditor from '@/components/pwa/ReviewsEditor';
import { PwaGeneratorModal, type PwaGeneratorSelection } from '@/components/pwa/PwaGeneratorModal';
import type { GeneratedPwaDraft } from '@/api/pwa';
import type { PwaReviewItem, PwaReviewI18nEntry } from '@/api/types';
import type { TrackerPwa } from '@/api/tracker-types';
import { getApps } from '@/api/apps';
import { listAccounts } from '@/api/firebase';
import { useAuthStore } from '@/store/auth';

type ModalMode = 'generate' | 'create' | 'edit' | 'delete' | null;

interface DescI18n {
  [lang: string]: { short: string; full: string };
}

interface FormData {
  app_id: string;
  name: string;
  app_name: string;
  developer_name: string;
  category: string;
  rating: number;
  reviews_count: number;
  downloads_count: string;
  size: string;
  version: string;
  short_description: string;
  full_description: string;
  icon_url: string;
  splash_url: string;
  bg_color: string;
  screenshots: string[];
  store_layout: 'auto' | 'play' | 'app';
  hide_google_branding: boolean;
  status: 'active' | 'paused';
  enabled_languages: string[];
  descriptions_i18n: DescI18n;
  fb_pixel_id: string;
  fb_access_token: string;
  fb_test_event_code: string;
  fb_events: Record<string, string>;
  reviews: PwaReviewItem[];
  reviews_i18n: Record<string, PwaReviewI18nEntry[]>;
  push_enabled: boolean;
  push_firebase_account_id: number | null;
  push_firebase_project_id: string;
}

const AVAILABLE_LANGUAGES = [
  { code: 'es', label: 'Spanish' },
  { code: 'zh', label: 'Chinese' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ar', label: 'Arabic' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'bn', label: 'Bengali' },
  { code: 'ru', label: 'Russian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'de', label: 'German' },
  { code: 'ko', label: 'Korean' },
  { code: 'fr', label: 'French' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'te', label: 'Telugu' },
  { code: 'tr', label: 'Turkish' },
  { code: 'ta', label: 'Tamil' },
  { code: 'it', label: 'Italian' },
  { code: 'th', label: 'Thai' },
  { code: 'pl', label: 'Polish' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'nl', label: 'Dutch' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'cs', label: 'Czech' },
  { code: 'ro', label: 'Romanian' },
  { code: 'el', label: 'Greek' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'sv', label: 'Swedish' },
  { code: 'he', label: 'Hebrew' },
  { code: 'da', label: 'Danish' },
  { code: 'fi', label: 'Finnish' },
  { code: 'no', label: 'Norwegian' },
  { code: 'sk', label: 'Slovak' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'hr', label: 'Croatian' },
  { code: 'sr', label: 'Serbian' },
  { code: 'sl', label: 'Slovenian' },
  { code: 'lt', label: 'Lithuanian' },
  { code: 'lv', label: 'Latvian' },
  { code: 'et', label: 'Estonian' },
];

const CATEGORIES = [
  'Tools', 'Entertainment', 'Social', 'Finance', 'Productivity',
  'Shopping', 'Communication', 'Education', 'Health & Fitness', 'Lifestyle',
  'Games', 'Music & Audio', 'Photography', 'Travel & Local', 'News & Magazines',
];

const initialFormData: FormData = {
  app_id: '',
  name: '',
  app_name: '',
  developer_name: 'Developer',
  category: 'Tools',
  rating: 4.5,
  reviews_count: 1000,
  downloads_count: '100K+',
  size: '12 MB',
  version: '1.0.0',
  short_description: '',
  full_description: '',
  icon_url: '',
  splash_url: '',
  bg_color: '#00031E',
  screenshots: [],
  store_layout: 'auto',
  hide_google_branding: false,
  status: 'active',
  enabled_languages: [],
  descriptions_i18n: {},
  fb_pixel_id: '',
  fb_access_token: '',
  fb_test_event_code: '',
  fb_events: { pageview: 'PageView', install_click: 'Lead', install_complete: 'InitiateCheckout', reg: 'CompleteRegistration', dep: 'Purchase' },
  reviews: [],
  reviews_i18n: {},
  push_enabled: false,
  push_firebase_account_id: null,
  push_firebase_project_id: '',
};

export function TrackerPwas() {
  const queryClient = useQueryClient();
  // Traffic managers work with PWAs as standalone entities, so the app link is
  // optional for them (see migration 127).
  const isTraffic = useAuthStore((s) => s.admin?.user_type) === 'traffic';
  const myAdminId = useAuthStore((s) => s.admin?.id);
  // Mirrors trackerAppOptional() on the worker: whoever is not filtered by app
  // may leave the PWA unattached. For an app-scoped admin the app link is the
  // only way back to the row, so it stays required for them.
  const appOptional = useAuthStore(
    (s) => s.admin?.user_type === 'traffic' || s.admin?.role === 'super_admin',
  );
  const grantedPwaIds = useAuthStore((s) => s.admin?.allowed_tracker_pwas);
  // Mirrors canModifyTrackerPwa on the worker: a traffic manager may edit their
  // own and explicitly granted PWAs, but not ones merely visible through a
  // campaign. Hiding the buttons avoids offering an action that 403s.
  function canEditPwa(p: TrackerPwa) {
    if (!isTraffic) return true;
    if (p.created_by_admin_id && p.created_by_admin_id === myAdminId) return true;
    return (grantedPwaIds ?? []).includes(p.id);
  }
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selected, setSelected] = useState<TrackerPwa | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState('');
  const [descTab, setDescTab] = useState<string>('en');
  const [translating, setTranslating] = useState(false);
  const [reviewsTranslating, setReviewsTranslating] = useState(false);

  const { data: pwas = [], isLoading } = useQuery({ queryKey: ['tracker-pwas'], queryFn: getTrackerPwas });
  const { data: apps = [] } = useQuery({ queryKey: ['apps'], queryFn: getApps });
  const { data: fbAccounts = [] } = useQuery({ queryKey: ['firebase-accounts'], queryFn: listAccounts });

  const createMutation = useMutation({
    mutationFn: (data: Partial<TrackerPwa> & { name: string }) => createTrackerPwa(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['tracker-pwas'] }); closeModal(); },
    onError: (e: Error) => setFormError(e.message),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TrackerPwa> }) => updateTrackerPwa(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['tracker-pwas'] }); closeModal(); },
    onError: (e: Error) => setFormError(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTrackerPwa(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['tracker-pwas'] }); closeModal(); },
    onError: (e: Error) => setFormError(e.message),
  });

  function closeModal() {
    setModalMode(null);
    setSelected(null);
    setFormData(initialFormData);
    setFormError('');
    setDescTab('en');
  }

  function openCreateModal() {
    setFormData(initialFormData);
    setFormError('');
    setDescTab('en');
    setSelected(null);
    setModalMode('create');
  }

  function useGeneratedDraft(draft: GeneratedPwaDraft, selection: PwaGeneratorSelection) {
    const generatedLanguage = draft.language_code.split('-')[0].toLowerCase();
    const localized = generatedLanguage !== 'en'
      && AVAILABLE_LANGUAGES.some((language) => language.code === generatedLanguage);
    setFormData({
      ...initialFormData,
      app_id: '',
      name: `${draft.app_name} · ${selection.geo}`,
      app_name: draft.app_name,
      developer_name: draft.developer_name,
      category: CATEGORIES.includes(draft.category) ? draft.category : 'Tools',
      rating: draft.rating,
      reviews_count: draft.reviews_count,
      downloads_count: draft.downloads_count,
      size: draft.size,
      version: draft.version,
      short_description: draft.short_description,
      full_description: draft.full_description,
      icon_url: draft.icon_url,
      splash_url: draft.splash_url,
      bg_color: draft.bg_color,
      screenshots: draft.screenshots,
      reviews: draft.reviews,
      enabled_languages: localized ? [generatedLanguage] : [],
      descriptions_i18n: localized ? {
        [generatedLanguage]: { short: draft.short_description, full: draft.full_description },
      } : {},
      reviews_i18n: localized ? {
        [generatedLanguage]: draft.reviews.map(({ text, reply }) => ({ text, ...(reply ? { reply } : {}) })),
      } : {},
      store_layout: draft.store_layout,
    });
    setSelected(null);
    setFormError('');
    setDescTab(localized ? generatedLanguage : 'en');
    setModalMode('create');
  }

  function openEditModal(p: TrackerPwa) {
    let screenshots: string[] = [];
    try { screenshots = JSON.parse(p.screenshots_json || '[]'); } catch { screenshots = []; }
    let descriptionsI18n: DescI18n = {};
    try { descriptionsI18n = JSON.parse(p.descriptions_i18n || '{}'); } catch { descriptionsI18n = {}; }
    let reviews: PwaReviewItem[] = [];
    let reviewsI18n: Record<string, PwaReviewI18nEntry[]> = {};
    try {
      const cfg = JSON.parse((p as any).reviews_json || '{}');
      if (Array.isArray(cfg?.items)) reviews = cfg.items;
      if (cfg && typeof cfg.i18n === 'object' && cfg.i18n) reviewsI18n = cfg.i18n;
    } catch {
      reviews = [];
      reviewsI18n = {};
    }
    const enabledLangs = Object.keys(descriptionsI18n).filter(
      k => AVAILABLE_LANGUAGES.some(l => l.code === k)
    );

    setFormData({
      app_id: p.app_id ?? '',
      name: p.name,
      app_name: p.app_name || '',
      developer_name: p.developer_name || 'Developer',
      category: p.category || 'Tools',
      rating: p.rating ?? 4.5,
      reviews_count: p.reviews_count ?? 1000,
      downloads_count: p.downloads_count || '100K+',
      size: p.size || '12 MB',
      version: p.version || '1.0.0',
      short_description: p.short_description || '',
      full_description: p.full_description || '',
      icon_url: p.icon_url || '',
      splash_url: p.splash_url || '',
      bg_color: p.bg_color || '#00031E',
      screenshots,
      store_layout: (p.store_layout as 'auto' | 'play' | 'app') || 'auto',
      hide_google_branding: !!p.hide_google_branding,
      status: (p.status as 'active' | 'paused') || 'active',
      enabled_languages: enabledLangs,
      descriptions_i18n: descriptionsI18n,
      fb_pixel_id: p.fb_pixel_id || '',
      fb_access_token: p.fb_access_token || '',
      fb_test_event_code: p.fb_test_event_code || '',
      fb_events: { pageview: 'PageView', install_click: 'Lead', install_complete: 'InitiateCheckout', reg: 'CompleteRegistration', dep: 'Purchase', ...(() => { try { return JSON.parse(p.fb_events_json || '{}'); } catch { return {}; } })() },
      reviews,
      reviews_i18n: reviewsI18n,
      push_enabled: !!p.push_enabled,
      push_firebase_account_id: p.push_firebase_account_id ?? null,
      push_firebase_project_id: p.push_firebase_project_id || '',
    });
    setSelected(p);
    setFormError('');
    setDescTab('en');
    setModalMode('edit');
  }

  function openDeleteModal(p: TrackerPwa) {
    setSelected(p);
    setFormError('');
    setModalMode('delete');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    if (!formData.name.trim()) { setFormError('Name is required'); return; }
    // A PWA is a standalone landing; only app-scoped roles must anchor it to an
    // app, because that is how they reach it at all.
    if (!appOptional && !formData.app_id) { setFormError('App and name are required'); return; }

    const i18nPayload: DescI18n = {};
    for (const lang of formData.enabled_languages) {
      if (formData.descriptions_i18n[lang]) i18nPayload[lang] = formData.descriptions_i18n[lang];
    }

    const payload: Partial<TrackerPwa> & { name: string } = {
      app_id: formData.app_id || null,
      name: formData.name.trim(),
      app_name: formData.app_name.trim(),
      developer_name: formData.developer_name.trim(),
      category: formData.category,
      rating: formData.rating,
      reviews_count: formData.reviews_count,
      downloads_count: formData.downloads_count,
      size: formData.size,
      version: formData.version,
      short_description: formData.short_description.trim(),
      full_description: formData.full_description.trim(),
      icon_url: formData.icon_url || null,
      splash_url: formData.splash_url || null,
      bg_color: formData.bg_color,
      screenshots_json: JSON.stringify(formData.screenshots),
      descriptions_i18n: JSON.stringify(i18nPayload),
      store_layout: formData.store_layout,
      hide_google_branding: formData.hide_google_branding ? 1 : 0,
      status: formData.status,
      fb_pixel_id: formData.fb_pixel_id.trim() || null,
      fb_access_token: formData.fb_access_token.trim() || null,
      fb_test_event_code: formData.fb_test_event_code.trim() || null,
      fb_events_json: JSON.stringify(formData.fb_events),
      reviews_json: JSON.stringify({ items: formData.reviews, i18n: formData.reviews_i18n }),
      push_enabled: formData.push_enabled ? 1 : 0,
      push_firebase_account_id: formData.push_firebase_account_id,
      push_firebase_project_id: formData.push_firebase_project_id.trim() || null,
    };

    if (modalMode === 'create') createMutation.mutate(payload);
    else if (modalMode === 'edit' && selected) updateMutation.mutate({ id: selected.id, data: payload });
  }

  const handleIconUpload = (file: File) => uploadPwaMedia(file);

  async function handleScreenshotUpload(file: File) {
    try {
      const url = await uploadPwaMedia(file);
      setFormData((prev) => ({ ...prev, screenshots: [...prev.screenshots, url] }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Upload failed');
    }
  }

  async function handleAutoTranslate() {
    if (!formData.short_description && !formData.full_description) {
      setFormError('Write English descriptions first before translating');
      return;
    }
    if (formData.enabled_languages.length === 0) {
      setFormError('Enable at least one language to translate to');
      return;
    }
    setTranslating(true);
    setFormError('');
    try {
      const result = await translateDescriptions(
        formData.short_description,
        formData.full_description,
        formData.enabled_languages
      );
      setFormData((prev) => ({
        ...prev,
        descriptions_i18n: { ...prev.descriptions_i18n, ...result },
      }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Translation failed');
    } finally {
      setTranslating(false);
    }
  }

  const handleReviewsChange = (items: PwaReviewItem[]) => {
    setFormData((prev) => ({ ...prev, reviews: items, reviews_i18n: {} }));
  };

  async function handleTranslateReviews() {
    if (formData.reviews.length === 0) {
      setFormError('Add at least one review before translating');
      return;
    }
    const targetLangs = AVAILABLE_LANGUAGES.map((l) => l.code);
    setReviewsTranslating(true);
    setFormError('');
    try {
      const result = await translateReviews(
        formData.reviews.map((r) => ({ text: r.text, reply: r.reply || '' })),
        targetLangs
      );
      setFormData((prev) => ({ ...prev, reviews_i18n: result }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Review translation failed');
    } finally {
      setReviewsTranslating(false);
    }
  }

  function toggleLanguage(langCode: string) {
    setFormData((prev) => {
      const enabled = prev.enabled_languages.includes(langCode)
        ? prev.enabled_languages.filter((l) => l !== langCode)
        : [...prev.enabled_languages, langCode];
      if (!enabled.includes(descTab) && descTab !== 'en') setDescTab('en');
      return { ...prev, enabled_languages: enabled };
    });
  }

  function removeScreenshot(index: number) {
    setFormData((prev) => ({ ...prev, screenshots: prev.screenshots.filter((_, i) => i !== index) }));
  }

  const columns = [
    {
      key: 'icon', label: '', render: (p: TrackerPwa) =>
        p.icon_url
          ? <img src={p.icon_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
          : <div className="w-10 h-10 rounded-lg bg-surface-muted" />,
    },
    {
      key: 'name', label: 'Name', render: (p: TrackerPwa) => (
        <div>
          <span className="font-medium text-content">{p.name}</span>
          <p className="text-xs text-content-subtle">{p.app_name || '-'} · {p.developer_name || '-'}</p>
        </div>
      ),
    },
    { key: 'category', label: 'Category', render: (p: TrackerPwa) => <span className="text-sm text-content-muted">{p.category || '-'}</span> },
    { key: 'store_layout', label: 'Layout', render: (p: TrackerPwa) => <span className="text-xs">{p.store_layout}</span> },
    {
      key: 'status', label: 'Status', render: (p: TrackerPwa) => {
        const styles: Record<string, string> = {
          active: 'bg-success-soft text-success',
          paused: 'bg-surface-muted text-content-muted',
        };
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[p.status] || ''}`}>
            {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
          </span>
        );
      },
    },
    {
      key: 'actions', label: '', render: (p: TrackerPwa) => !canEditPwa(p) ? null : (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); openEditModal(p); }}
            className="text-content-subtle hover:text-content-muted transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); openDeleteModal(p); }}
            className="text-content-subtle hover:text-danger transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Tracker PWAs</h1>
          <p className="text-sm text-content-subtle mt-1">Store-style landings for tracker campaigns (destination=pwa)</p>
        </div>
        <EditOnly>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={openCreateModal}>Create manually</Button>
            <Button onClick={() => setModalMode('generate')}>
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 3l1.3 4.1L17 9l-3.7 1.9L12 15l-1.3-4.1L7 9l3.7-1.9L12 3zM5 14l.8 2.2L8 17l-2.2.8L5 20l-.8-2.2L2 17l2.2-.8L5 14zM19 13l.8 2.2L22 16l-2.2.8L19 19l-.8-2.2L16 16l2.2-.8L19 13z" />
              </svg>
              Generate PWA
            </Button>
          </div>
        </EditOnly>
      </div>

      <DataTable
        columns={columns}
        data={pwas}
        loading={isLoading}
        keyExtractor={(p) => p.id}
        onRowClick={(p) => { if (canEditPwa(p)) openEditModal(p); }}
      />

      <PwaGeneratorModal
        isOpen={modalMode === 'generate'}
        onClose={closeModal}
        onUseDraft={useGeneratedDraft}
      />

      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Create tracker PWA' : 'Edit tracker PWA'}
        size="full"
      >
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          {formError && (
            <div className="p-2 bg-danger-soft border border-danger/30 rounded-md mb-3 flex-shrink-0">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: App Settings */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-content-subtle mb-1">
                  {appOptional ? 'App' : 'App *'}
                </label>
                <Select
                  value={formData.app_id}
                  onChange={(v) => setFormData((prev) => ({ ...prev, app_id: v }))}
                  searchable
                  size="sm"
                  options={[
                    { value: '', label: appOptional ? 'No app (standalone)' : 'Select app' },
                    ...apps.map((app) => ({ value: app.id, label: app.name })),
                  ]}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-content-subtle mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Internal name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">App name</label>
                  <input
                    type="text"
                    value={formData.app_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, app_name: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Shown in store"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Developer</label>
                  <input
                    type="text"
                    value={formData.developer_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, developer_name: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Category</label>
                  <Select
                    value={formData.category}
                    onChange={(v) => setFormData((prev) => ({ ...prev, category: v }))}
                    searchable
                    size="sm"
                    options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Status</label>
                  <Select
                    value={formData.status}
                    onChange={(v) => setFormData((prev) => ({ ...prev, status: v as 'active' | 'paused' }))}
                    size="sm"
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'paused', label: 'Paused' },
                    ]}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-content-subtle mb-1">Store layout</label>
                <Select
                  value={formData.store_layout}
                  onChange={(v) => setFormData((prev) => ({ ...prev, store_layout: v as 'auto' | 'play' | 'app' }))}
                  size="sm"
                  options={[
                    { value: 'auto', label: 'Auto (by device)' },
                    { value: 'play', label: 'Play (Google Play)' },
                    { value: 'app', label: 'App (App Store)' },
                  ]}
                />
                <label className="inline-flex items-start gap-2 cursor-pointer select-none mt-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={formData.hide_google_branding}
                    onChange={(e) => setFormData((prev) => ({ ...prev, hide_google_branding: e.target.checked }))}
                  />
                  <span className="text-sm">
                    Hide Google branding
                    <span className="block text-xs text-content-subtle">
                      Removes the Play logo, the "Google Play" wordmark, the "Apps on Google Play" title and the Play Protect line. App Store layout is unaffected.
                    </span>
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Rating</label>
                  <input
                    type="number"
                    value={formData.rating}
                    onChange={(e) => setFormData((prev) => ({ ...prev, rating: parseFloat(e.target.value) || 0 }))}
                    min={1}
                    max={5}
                    step={0.1}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Reviews</label>
                  <input
                    type="number"
                    value={formData.reviews_count}
                    onChange={(e) => setFormData((prev) => ({ ...prev, reviews_count: parseInt(e.target.value) || 0 }))}
                    min={0}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Downloads</label>
                  <input
                    type="text"
                    value={formData.downloads_count}
                    onChange={(e) => setFormData((prev) => ({ ...prev, downloads_count: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="100K+"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Size</label>
                  <input
                    type="text"
                    value={formData.size}
                    onChange={(e) => setFormData((prev) => ({ ...prev, size: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="12 MB"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Version</label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) => setFormData((prev) => ({ ...prev, version: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="1.0.0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">BG Color</label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="color"
                      value={formData.bg_color}
                      onChange={(e) => setFormData((prev) => ({ ...prev, bg_color: e.target.value }))}
                      className="w-8 h-8 rounded border border-border-strong cursor-pointer p-0.5 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={formData.bg_color}
                      onChange={(e) => setFormData((prev) => ({ ...prev, bg_color: e.target.value }))}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="#00031E"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Middle: Media */}
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-content-subtle">Icon</label>
                  <span className="text-xs text-content-subtle">512x512 PNG</span>
                </div>
                <MediaUpload
                  value={formData.icon_url}
                  onChange={(url) => setFormData((prev) => ({ ...prev, icon_url: url }))}
                  onUpload={handleIconUpload}
                  accept="image/*"
                  mediaType="image"
                />
              </div>

              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-content-subtle">Splash Screen</label>
                  <span className="text-xs text-content-subtle">Full-screen launch image</span>
                </div>
                <MediaUpload
                  value={formData.splash_url}
                  onChange={(url) => setFormData((prev) => ({ ...prev, splash_url: url }))}
                  onUpload={handleIconUpload}
                  accept="image/*"
                  mediaType="image"
                />
              </div>

              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-content-subtle">Screenshots</label>
                  <label className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary border border-primary/30 rounded-md cursor-pointer hover:bg-primary/5 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleScreenshotUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>

                {formData.screenshots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {formData.screenshots.map((url, i) => (
                      <div key={i} className="relative group">
                        <img src={url} alt="" className="w-full h-28 rounded-md object-cover" />
                        <button
                          type="button"
                          onClick={() => removeScreenshot(i)}
                          className="absolute top-1 right-1 w-5 h-5 bg-danger text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-danger"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <p className="text-xs text-content-subtle">No screenshots added</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Descriptions & i18n */}
            <div className="space-y-3 min-h-0 flex flex-col">
              <div>
                <label className="text-xs font-medium text-content-subtle mb-1 block">Languages</label>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 max-h-32 overflow-y-auto border border-border rounded-md p-2">
                  {AVAILABLE_LANGUAGES.map((lang) => (
                    <label key={lang.code} className="flex items-center gap-1.5 text-xs text-content-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.enabled_languages.includes(lang.code)}
                        onChange={() => toggleLanguage(lang.code)}
                        className="rounded border-border-strong text-primary focus:ring-primary w-3.5 h-3.5"
                      />
                      {lang.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-1 border-b border-border pb-1">
                <button
                  type="button"
                  onClick={() => setDescTab('en')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    descTab === 'en' ? 'bg-primary/10 text-primary' : 'text-content-subtle hover:text-content-muted hover:bg-surface-muted'
                  }`}
                >
                  EN
                </button>
                {formData.enabled_languages.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setDescTab(code)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                      descTab === code ? 'bg-primary/10 text-primary' : 'text-content-subtle hover:text-content-muted hover:bg-surface-muted'
                    }`}
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>

              {descTab === 'en' && (
                <div className="space-y-2 flex-1 flex flex-col">
                  <div>
                    <label className="block text-xs font-medium text-content-subtle mb-1">Short Description</label>
                    <textarea
                      value={formData.short_description}
                      onChange={(e) => setFormData((prev) => ({ ...prev, short_description: e.target.value }))}
                      rows={2}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Brief description of the app"
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <label className="block text-xs font-medium text-content-subtle mb-1">Full Description</label>
                    <textarea
                      value={formData.full_description}
                      onChange={(e) => setFormData((prev) => ({ ...prev, full_description: e.target.value }))}
                      rows={8}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary flex-1"
                      placeholder="Detailed description"
                    />
                  </div>
                </div>
              )}

              {descTab !== 'en' && (
                <div className="space-y-2 flex-1 flex flex-col">
                  <div>
                    <label className="block text-xs font-medium text-content-subtle mb-1">Short Description</label>
                    <textarea
                      value={formData.descriptions_i18n[descTab]?.short || ''}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        descriptions_i18n: {
                          ...prev.descriptions_i18n,
                          [descTab]: { ...prev.descriptions_i18n[descTab], short: e.target.value },
                        },
                      }))}
                      rows={2}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Translated short description"
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <label className="block text-xs font-medium text-content-subtle mb-1">Full Description</label>
                    <textarea
                      value={formData.descriptions_i18n[descTab]?.full || ''}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        descriptions_i18n: {
                          ...prev.descriptions_i18n,
                          [descTab]: { ...prev.descriptions_i18n[descTab], full: e.target.value },
                        },
                      }))}
                      rows={8}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary flex-1"
                      placeholder="Translated full description"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAutoTranslate}
                    disabled={translating}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-start"
                  >
                    {translating ? (
                      <>
                        <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Translating...
                      </>
                    ) : 'Auto-translate from English'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Reviews */}
          <ReviewsEditor
            items={formData.reviews}
            i18n={formData.reviews_i18n}
            onItemsChange={handleReviewsChange}
            onTranslate={handleTranslateReviews}
            translating={reviewsTranslating}
            uploadPhoto={uploadPwaMedia}
          />

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-sm font-semibold mb-3">Push notifications</h3>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none mb-3">
              <input
                type="checkbox"
                checked={formData.push_enabled}
                onChange={(e) => setFormData({ ...formData, push_enabled: e.target.checked })}
              />
              <span className="text-sm">Enable web push for installed landings</span>
            </label>
            <label className="block text-xs font-medium text-content-subtle mb-1">Firebase account</label>
            <select
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3 bg-white dark:bg-gray-800"
              value={formData.push_firebase_account_id ?? ''}
              onChange={(e) => setFormData({ ...formData, push_firebase_account_id: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— none —</option>
              {fbAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <label className="block text-xs font-medium text-content-subtle mb-1">Firebase project id (for sending)</label>
            <input
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3"
              type="text"
              value={formData.push_firebase_project_id}
              placeholder="my-firebase-project"
              onChange={(e) => setFormData({ ...formData, push_firebase_project_id: e.target.value })}
            />
            <p className="text-xs text-content-subtle">
              The selected Firebase account must have web credentials (API key, sender id, app id, VAPID key)
              configured on the Firebase page. Visitors are asked for permission only after they install the
              landing, so the prompt never sits in front of the install button.
            </p>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-sm font-semibold mb-3">Facebook Pixel</h3>
            <label className="block text-xs font-medium text-content-subtle mb-1">Pixel ID</label>
            <input
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3"
              value={formData.fb_pixel_id}
              onChange={(e) => setFormData({ ...formData, fb_pixel_id: e.target.value })}
              placeholder="e.g. 1234567890"
            />
            <label className="block text-xs font-medium text-content-subtle mb-1">Access Token (Conversions API)</label>
            <input
              type="password"
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3"
              value={formData.fb_access_token}
              onChange={(e) => setFormData({ ...formData, fb_access_token: e.target.value })}
              placeholder="EAAB..."
              autoComplete="off"
            />
            <label className="block text-xs font-medium text-content-subtle mb-1">Test Event Code (optional)</label>
            <input
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3"
              value={formData.fb_test_event_code}
              onChange={(e) => setFormData({ ...formData, fb_test_event_code: e.target.value })}
              placeholder="TEST12345"
            />
            <div className="grid grid-cols-2 gap-3">
              {(['pageview', 'install_click', 'install_complete', 'reg', 'dep'] as const).map((k) => (
                <div key={k}>
                  <label className="block text-xs text-gray-500 mb-1">{k} event name</label>
                  <input
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    value={formData.fb_events[k] ?? ''}
                    onChange={(e) => setFormData({ ...formData, fb_events: { ...formData.fb_events, [k]: e.target.value } })}
                    placeholder="(empty = off)"
                  />
                </div>
              ))}
            </div>
          </div>
          </div>

          <div className="flex justify-end gap-3 pt-3 mt-3 border-t border-border flex-shrink-0">
            <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {modalMode === 'create' ? 'Create PWA' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={modalMode === 'delete'}
        onClose={closeModal}
        title="Delete tracker PWA"
        size="sm"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}
          <p className="text-sm text-content-muted">
            Are you sure you want to delete <strong>{selected?.name}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => selected && deleteMutation.mutate(selected.id)}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
